import { type FailedWrite } from '~/features/credit-ledger';
/**
 * **Why the server said no to a change of hours or settings** — which act failed, and why, as keys.
 *
 * Every write on the Grafik and Ustawienia tabs is refused through one of the codes the controllers
 * declare (`TrainerAvailabilityController`, `BookingPolicyController` `[Refuses]`), and each is worded
 * in mobile's own sentences (`booking.trainer.windowError.*`, `…saveError.*`). The act comes first
 * — „Nie zapisano tygodnia." — because a refusal that only says why leaves the trainer guessing
 * what did not happen. A code this build does not know is worded by its status, and no answer at
 * all is said as such, never as something the server decided.
 *
 * Pure: no React, no DOM, no query hook, no i18next. Words are i18n KEYS.
 */

import { MAX_WEEKLY_WINDOWS } from './weekPattern';

export type ScheduleAct =
  'saveWeek' | 'saveException' | 'removeException' | 'saveBlock' | 'removeBlock' | 'savePolicy';

export interface ScheduleRefusal {
  readonly actKey: string;
  readonly reasonKey: string;
  readonly params: Readonly<Record<string, number>>;
}

const TRAINER = 'web:booking.trainer';
const SCHEDULE = 'web:booking.schedule';

const NAMED: Readonly<Record<string, { key: string; params?: Record<string, number> }>> = {
  // The server's overlap names no hours; mobile's plain sentence says what to do without them.
  window_overlap: { key: `${TRAINER}.windowError.window_overlap.plain` },
  window_crosses_midnight: { key: `${TRAINER}.windowError.window_crosses_midnight.text` },
  window_empty: { key: `${TRAINER}.windowError.window_empty.text` },
  windows_too_many: {
    key: `${TRAINER}.windowError.windows_too_many.text`,
    params: { max: MAX_WEEKLY_WINDOWS },
  },
  availability_too_many: { key: `${TRAINER}.saveError.availability_too_many.text` },
  availability_range_invalid: { key: `${TRAINER}.saveError.availability_range_invalid.text` },
  block_range_invalid: { key: `${TRAINER}.saveError.block_range_invalid.text` },
  // A policy value off R1's lists or outside its bounds: this build offers none, so it is a value
  // the server narrowed since — said plainly, and the tab reads the policy again.
  booking_value_not_allowed: { key: `${SCHEDULE}.reason.valueRefused` },
  booking_value_out_of_range: { key: `${SCHEDULE}.reason.valueRefused` },
};

/** A refusal that names no code this build knows, worded by its status — or no answer at all. */
function reasonByStatus(failure: FailedWrite | null): string {
  if (failure === null) return `${SCHEDULE}.reason.unreached`;
  if (failure.status === 403) return `${SCHEDULE}.reason.forbidden`;
  if (failure.status === 400 || failure.status === 409 || failure.status === 422) {
    return `${SCHEDULE}.reason.invalid`;
  }
  return `${SCHEDULE}.reason.other`;
}

export function scheduleRefusal(act: ScheduleAct, failure: FailedWrite | null): ScheduleRefusal {
  const actKey = `${SCHEDULE}.failed.${act}`;
  const named = failure?.code === undefined ? undefined : NAMED[failure.code];
  if (named !== undefined) return { actKey, reasonKey: named.key, params: named.params ?? {} };
  return { actKey, reasonKey: reasonByStatus(failure), params: {} };
}
